import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IResultado, ITipoOpcion } from './interfaces/resultado.interface';
import { ResultadoService } from './services/resultado.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-resultado-form',
  templateUrl: './resultado-form.component.html',
  providers: [MessageService]
})
export class ResultadoFormComponent implements OnInit, OnChanges {
  @Input() model: IResultado | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;

  // Opciones para el dropdown de tipo
  tipoOptions: { label: string; value: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private resultadoService: ResultadoService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadTiposOpciones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadResultado();
      } else {
        this.initForm();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      sResultado: [{ value: '', disabled: !!this.model }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      efectivo: [false]
    });
  }

  loadTiposOpciones(): void {
    this.resultadoService.getTiposOpciones().subscribe({
      next: (response) => {
        if (response.success) {
          // Mapear las opciones del servidor al formato del dropdown
          this.tipoOptions = response.data.map((opcion: ITipoOpcion) => ({
            label: opcion.sOpcion,
            value: opcion.sValor
          }));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las opciones de tipo'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las opciones de tipo'
        });
      }
    });
  }

  loadResultado(): void {
    if (!this.model?.sResultado) return;
    
    this.loading = true;
    // TODO: Implementar carga de resultado cuando se tenga el servicio
    setTimeout(() => {
      this.form.patchValue({
        sResultado: this.model?.sResultado,
        descripcion: this.model?.descripcion,
        efectivo: this.model?.efectivo === 1
      });
      this.form.get('sResultado')?.disable();
      this.loading = false;
    }, 500);
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const efectivoValue = formData.efectivo ? 1 : 0;

    this.resultadoService.createResultado(formData.sResultado, formData.descripcion, efectivoValue).subscribe({
      next: (response) => {
        if (response.success || (response.data?.[0]?.error === "0")) {
          this.onSave.emit({
            success: true,
            message: 'Resultado creado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.[0]?.error || 'Error al crear el resultado'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el resultado'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const efectivoValue = formData.efectivo ? 1 : 0;

    this.resultadoService.updateResultado(formData.sResultado, formData.descripcion, efectivoValue).subscribe({
      next: (response) => {
        if (response.success || (response.data?.[0]?.error === "0")) {
          this.onSave.emit({
            success: true,
            message: 'Resultado actualizado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.data?.[0]?.error || 'Error al actualizar el resultado'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el resultado'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'ResCob',
        sClave: `${this.form.get('sResultado')?.value}`,
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 